// #tag: STUDENT VAULT API CLIENT
// #purpose: Dispatches API requests for student document CRUD, batch deletions, category extensions, AI analysis jobs, and binary downloads.
import { apiDelete, apiGet, apiPost, apiPut, fetchBlobWithProgress } from './client';
import type { VaultDocumentDto } from './cms';
import type { DocumentAnalysisResult } from '../types/documentIntelligence';

export type { VaultDocumentDto };

export interface UploadVaultDocumentInput {
  name: string;
  categoryId: string;
  subcategory: string;
  expiryDate?: string;
  fileName: string;
  mimeType: string;
  fileData: string;
  tags?: string[];
  description?: string;
  detectedDocType?: string;
  aiMetadata?: DocumentAnalysisResult;
  aiAnalysisStatus?: string;
}

export function fetchVaultDocuments(params?: { q?: string; categoryId?: string }): Promise<{ data: VaultDocumentDto[] }> {
  const sp = new URLSearchParams();
  if (params?.q) sp.set('q', params.q);
  if (params?.categoryId && params.categoryId !== 'all') sp.set('categoryId', params.categoryId);
  const qs = sp.toString();
  return apiGet(`/vault/documents${qs ? `?${qs}` : ''}`);
}

export function uploadVaultDocument(input: UploadVaultDocumentInput): Promise<VaultDocumentDto> {
  return apiPost('/vault/documents', input);
}

export function renameVaultDocument(id: string, name: string): Promise<VaultDocumentDto> {
  return apiPut(`/vault/documents/${id}`, { name });
}

export function replaceVaultDocument(id: string, input: Partial<UploadVaultDocumentInput>): Promise<VaultDocumentDto> {
  return apiPut(`/vault/documents/${id}`, input);
}

export function deleteVaultDocument(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/vault/documents/${id}`);
}

export function deleteVaultDocuments(ids: string[]): Promise<{ ok: boolean; deleted: number }> {
  return apiPost('/vault/documents/bulk-delete', { ids });
}

export function reorderVaultDocument(id: string, position: number): Promise<VaultDocumentDto> {
  return apiPost('/vault/documents/reorder', { id, position });
}

export function fetchVaultFileBlob(id: string, onProgress?: (percent: number) => void): Promise<Blob> {
  return fetchBlobWithProgress(`/vault/documents/${id}/file`, onProgress);
}

export function addVaultCategoryItem(categoryId: string, item: string): Promise<{ id: string; items: string[] }> {
  return apiPost(`/vault/categories/${categoryId}/items`, { item });
}

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read file'));
        return;
      }
      const base64 = result.split(',')[1];
      if (!base64) {
        reject(new Error('Could not encode file'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
